/**
 * Typed engine errors. Nothing here throws a string: a caller (and a Cloud Function
 * trigger) has to be able to branch on *why* a request was refused without parsing prose.
 */
/** Base class for everything the engine throws, so a caller can catch the engine alone. */
export class EngineError extends Error {
    constructor(message) {
        super(message);
        this.name = new.target.name;
    }
}
/** A request the engine will not act on — malformed, or self-contradictory. */
export class InvalidRequestError extends EngineError {
}
/** A create-only write lost a race: the id already exists. Callers re-read and use theirs. */
export class AlreadyExistsError extends EngineError {
    id;
    constructor(id) {
        super(`record already exists: ${id}`);
        this.id = id;
    }
}
/**
 * A stored document could not be read as the type it claims to be.
 *
 * Deliberately separate from `InvalidRequestError`: that one is "you asked for something
 * impossible", this one is "what came back from storage is not a record". A surface turns
 * it into "this piece can't be opened", never into a half-built object.
 */
export class MalformedRecordError extends EngineError {
    /** The record type that was expected, e.g. `'primitive'`. */
    recordType;
    id;
    constructor(recordType, id, detail) {
        super(`malformed ${recordType} ${id}: ${detail}`);
        this.recordType = recordType;
        this.id = id;
    }
}
/** An update targeted a record that is not there. */
export class NotFoundError extends EngineError {
    id;
    constructor(id) {
        super(`record not found: ${id}`);
        this.id = id;
    }
}
/**
 * A variant (or a scene render) was refused because the primitive's identity is not locked.
 * Thrown before any store write, so a refused request never costs money or leaves a record.
 */
export class VariantNotAllowedError extends EngineError {
    reason;
    primitiveId;
    constructor(primitiveId, reason, message) {
        super(message);
        this.primitiveId = primitiveId;
        this.reason = reason;
    }
}
/** The approval step was asked to do something it may not — approve a non-base or unready render. */
export class ApprovalError extends EngineError {
}
/**
 * A primitive's axes describe a matrix that would be too large to be honest about.
 *
 * Axes are combinatorial and they multiply, so the guardrail is a hard bound rather than
 * advice (PRD 02 §12). The limit that was hit is on the error, so the Studio can say which.
 */
export class AxesTooLargeError extends EngineError {
    limit;
    actual;
    allowed;
    constructor(limit, actual, allowed, message) {
        super(message);
        this.limit = limit;
        this.actual = actual;
        this.allowed = allowed;
    }
}
//# sourceMappingURL=errors.js.map